import {
    Component,
    Element,
    Host,
    h,
    Fragment,
    Listen,
    State,
} from "@stencil/core";
import { debounce } from "../../utils/debounce";

/**
 * @slot (default) - Slot for the table (`<table>`)
 * @slot headline - Slot for the headline of the product comparison section.
 */
@Component({
    tag: "ux-iws-nb-product-comparison",
    styleUrl: "nb-product-comparison.scss",
    shadow: false,
})
export class NbProductComparison {
    private static uidIncrement = 0;
    private uid = `nb-product-comparison-${NbProductComparison.uidIncrement++}`;
    private baseClass = "ux-iws-nb-product-comparison";

    @Element() host: HTMLUxIwsNbProductComparisonElement;

    @State() private mode: "table" | "cards" = "table";

    @Listen("resize", { target: "window" })
    onResize() {
        if (this.mode === "cards") {
            this.syncCardCellHeightDebounced();
        }
    }

    /** Media query to determine if the viewport is wide enough for desktop layout */
    private mediaQuery = window.matchMedia("(min-width: 1440px)");

    /** Storage for the table data */
    private tableData: string[][] = [];

    /**
     * Sets the `mode` state based on the media query.
     * This determines if the component should render in desktop mode or not.
     */
    private setMode = () => {
        this.mode = this.mediaQuery.matches ? "table" : "cards";
    };

    /**
     * Creates a 2D array from the table data.
     * The first row contains the column headers, and the first column contains the row headers.
     * @param table - The HTML table element to extract data from.
     * @returns A 2D array representing the table data.
     */
    private createTableData = (table: HTMLTableElement): string[][] => {
        const productRows: string[][] = [];
        if (!(table instanceof HTMLTableElement)) {
            return productRows;
        }
        const rows = table.querySelectorAll("tr");
        rows.forEach((tr, row) => {
            const cells = tr.querySelectorAll<HTMLElement>("th, td");
            cells.forEach((cell, col) => {
                if (!Array.isArray(productRows[col])) {
                    productRows[col] = [];
                }
                productRows[col][row] = cell.innerHTML;
            });
        });
        return productRows;
    };

    /**
     * Synchronizes the heights of card cells for each row across all product cards.
     * Loops through all product cards and sets the height of each cell in a row to the maximum height found in that row.
     */
    private syncCardCellHeight = () => {
        if (this.mode !== "cards") {
            return;
        }
        const cards = Array.from(
            this.host.querySelectorAll(`.${this.baseClass}__card`)
        );
        if (cards.length === 0) {
            return;
        }
        const rowCount = cards[0].querySelectorAll(
            `.${this.baseClass}__cell`
        ).length;
        // Reset all cell heights
        cards.forEach((card) => {
            const cells = card.querySelectorAll(`.${this.baseClass}__cell`);
            cells.forEach((cell) => {
                (cell as HTMLElement).style.height = "";
            });
        });
        // For each row, find the max height and set it for all cells in that row
        for (let row = 0; row < rowCount; row++) {
            let maxHeight = 0;
            const rowCells = cards.map((card) => {
                const cells = card.querySelectorAll(`.${this.baseClass}__cell`);
                return cells[row] as HTMLElement;
            });
            rowCells.forEach((cell) => {
                if (cell) {
                    cell.style.height = "";
                    maxHeight = Math.max(maxHeight, cell.offsetHeight);
                }
            });
            rowCells.forEach((cell) => {
                if (cell) {
                    cell.style.height = `${maxHeight}px`;
                }
            });
        }
    };

    /**
     * Debounced version of syncCardCellHeight.
     */
    private syncCardCellHeightDebounced = debounce(
        this.syncCardCellHeight,
        100
    );

    /**
     * Renders product cards based on the provided table data.
     * Each card represents a product with its details.
     * @param tableData - The 2D array containing the table data.
     * @returns A fragment containing the rendered product cards.
     */
    private renderProductCards = (tableData: string[][]) => (
        <Fragment>
            {tableData.slice(1).map((rows, index) => (
                <div
                    key={`product-${index}`}
                    class={`${this.baseClass}__card`}
                    data-slide={index + 1}
                >
                    <div
                        class={`${this.baseClass}__cell ${this.baseClass}__cell--header`}
                        innerHTML={rows[0]}
                    />
                    <div
                        class={`${this.baseClass}__cell ${this.baseClass}__cell--header`}
                        innerHTML={rows[1]}
                    />
                    {rows.slice(2).map((cell, cellIndex) => (
                        <div
                            key={`product-${index}-row-${cellIndex}`}
                            class={`${this.baseClass}__cell`}
                        >
                            <div>
                                <span
                                    class={`${this.baseClass}__cell-label`}
                                    innerHTML={tableData[0][cellIndex + 2]}
                                />
                                <span
                                    class={`${this.baseClass}__cell-value`}
                                    innerHTML={cell}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            ))}
        </Fragment>
    );

    componentWillLoad() {
        this.tableData = this.createTableData(this.host.querySelector("table"));
        this.mediaQuery.addEventListener("change", this.setMode);
        this.setMode();
    }

    componentDidRender() {
        this.syncCardCellHeight();
    }

    render() {
        const hasHeadline = !!this.host.querySelector(`[slot="headline"]`);
        return (
            <Host data-mode={this.mode}>
                <div
                    class={
                        hasHeadline ? `${this.baseClass}__header` : "is-hidden"
                    }
                >
                    <slot name="headline" />
                </div>
                <section class={`${this.baseClass}__container`} id={this.uid}>
                    {this.mode === "cards" && (
                        <ux-iws-horizontal-scroll-slider noArrows={true}>
                            <div
                                class={`${this.baseClass}__cards`}
                                role="presentation"
                            >
                                {this.renderProductCards(this.tableData)}
                            </div>
                        </ux-iws-horizontal-scroll-slider>
                    )}
                    <div
                        class={`${this.baseClass}__table`}
                        aria-hidden={this.mode !== "table" ? "true" : "false"}
                    >
                        <slot />
                    </div>
                </section>
            </Host>
        );
    }
}
